import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useOnline } from "@/lib/useOnline";
import {
  LayoutDashboard, ArrowDownToLine, ArrowUpFromLine, Boxes, Package,
  Users, FileText, Settings, LogOut, Menu, X, Wifi, WifiOff, Wheat,
  ArrowLeftRight, ReceiptIndianRupee, HandCoins, Wallet, FileUp,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/inward", label: "Inward Entry", icon: ArrowDownToLine },
  { to: "/outward", label: "Outward Entry", icon: ArrowUpFromLine },
  { to: "/transfer", label: "Stock Transfer", icon: ArrowLeftRight },
  { to: "/stacks", label: "Stack Layout", icon: Boxes },
  { to: "/lots", label: "Lots", icon: Package },
  { to: "/parties", label: "Parties & Ledger", icon: Users },
  { to: "/rent", label: "Rent Billing", icon: ReceiptIndianRupee },
  { to: "/payments", label: "Payments", icon: HandCoins },
  { to: "/cash", label: "Manager Cash", icon: Wallet },
  { to: "/registers", label: "Registers", icon: FileText },
  { to: "/onboard", label: "Onboarding", icon: FileUp },
  { to: "/masters", label: "Masters", icon: Settings },
];

export default function Layout({ children }) {
  const { user, logout, canPage } = useAuth();
  const online = useOnline();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const links = NAV.filter((n) => canPage(n.to));

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-emerald-800/40">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-700 text-white">
          <Wheat size={22} strokeWidth={2} />
        </div>
        <div className="leading-tight">
          <div className="font-display font-extrabold text-white text-[15px] tracking-tight">SRM WMS</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">SRM Agri Warehouse</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {links.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-emerald-700 text-white"
                  : "text-emerald-100/80 hover:bg-emerald-800/60 hover:text-white"
              }`
            }
          >
            <n.icon size={18} strokeWidth={1.8} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-emerald-800/40 p-3">
        <div className="mb-2 rounded-lg bg-emerald-800/50 px-3 py-2">
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-[11px] text-emerald-300">{user?.role}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-emerald-100 hover:bg-emerald-800/60 transition-colors"
        >
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-stone-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-emerald-950 fixed inset-y-0 left-0 z-30 print:hidden">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-emerald-950 animate-fade-up">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0 print:ml-0">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-stone-200 bg-white/80 backdrop-blur-xl px-4 md:px-6 h-16 print:hidden">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div className="font-display font-bold text-stone-800 text-lg hidden md:block">
            Warehouse Management System
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <span
              data-testid="connection-status"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                online ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
            >
              {online ? <Wifi size={13} /> : <WifiOff size={13} />}
              {online ? "Online" : "Offline"}
            </span>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
